import { mentionToMarkerText } from './markers';
import { MentionType } from './types';

/**
 * Attributes the copy side (`handleCopyDrawer` in the terminal app) stamps
 * onto the insight link it appends to copied HTML, so paste can recognize
 * "this clipboard content is an insight" and collapse it into a chip
 * instead of dumping the full drawer HTML.
 */
export const INSIGHT_ID_ATTR = 'data-insight-id';
export const INSIGHT_TITLE_ATTR = 'data-insight-title';
export const INSIGHT_CATEGORY_ATTR = 'data-insight-category';

/**
 * "Category" matches the terminal app's own domain term for this —
 * `KgInsight.category` — extended with `'scenario'` as a pseudo-category
 * for scenario insights, which aren't `KgInsight`s and have no `category`
 * field of their own.
 */
const CATEGORY_TO_MENTION_TYPE: Record<string, MentionType> = {
  scenario: 'insight-scenario',
  company_catalyst: 'insight-catalyst-company',
  market_catalyst: 'insight-catalyst-market',
  earnings_preview: 'insight-earnings-preview',
  earnings_recap: 'insight-earnings-recap',
};

/**
 * Base domains the app is actually deployed under (see `WEBSITE_DOMAIN` in
 * `containerize-teams.yml`: prod is `reflexivity.com`, staging is
 * `rflx.co.uk`), plus `localhost` for local dev.
 */
const TRUSTED_INSIGHT_URL_HOST_SUFFIXES = ['reflexivity.com', 'rflx.co.uk'];

/**
 * Insight chips render as links to their `id`/href, so anything accepted
 * here becomes an implicitly-trusted, official-looking link. Restrict to
 * our own domains so pasted HTML — or a hand-typed `@[label](insight:url)`
 * marker — can't smuggle an arbitrary external URL behind an insight chip.
 */
export const isTrustedInsightUrl = (url: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return false;
  }

  return (
    parsed.hostname === 'localhost' ||
    TRUSTED_INSIGHT_URL_HOST_SUFFIXES.some(
      suffix =>
        parsed.hostname === suffix || parsed.hostname.endsWith(`.${suffix}`)
    )
  );
};

/**
 * Returns the `@[title](insight...:url)` wire marker for a pasted insight,
 * or `null` when the pasted HTML doesn't carry an insight marker.
 *
 * The mention's `id` is the insight's share URL (not the raw insight ID) so
 * `ResolvedMentionChip` can render the chip as a direct link back to the
 * insight without needing to re-derive the KG-vs-scenario share path.
 *
 * The mention's `type` is derived from `data-insight-category` so the chip
 * renders with the same icon/color as the insight's own category badge
 * (`CardPill`, e.g. `ExploreNav`/indigo for scenario insights). Unknown or
 * missing categories fall back to the generic `insight` type.
 */
export const extractInsightMentionText = (html: string): string | null => {
  const marker = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(`[${INSIGHT_ID_ATTR}]`);

  const insightId = marker?.getAttribute(INSIGHT_ID_ATTR);
  const url = marker?.getAttribute('href');
  const title =
    marker?.getAttribute(INSIGHT_TITLE_ATTR) || marker?.textContent?.trim();

  if (!insightId || !url || !title || !isTrustedInsightUrl(url)) {
    return null;
  }

  const category = marker?.getAttribute(INSIGHT_CATEGORY_ATTR) ?? '';

  return mentionToMarkerText({
    type: CATEGORY_TO_MENTION_TYPE[category] ?? 'insight',
    id: url,
    name: title,
    label: title,
  });
};
